'use client';

import { Avatar } from '@repo/ui/avatar';
import { Button } from '@repo/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover';
import { TabItem } from '@repo/ui/tab-item';
import { cn } from '@repo/utils/cn';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { getAccountMenuItems } from '@/app/[locale]/(account)/account/components/account-menu/account-menu-items';
import { AVATARS } from '@/app/[locale]/(account)/account/components/edit-user-avatar/constants';
import { UnStyledLink } from '@/components/common/link';
import SignOutButton from '@/components/forms/sign-out/sign-out-button';
import { usePathname } from '@/i18n/routing';

interface AccountMenuPopoverProps {
  avatar: string | null;
}

const AccountMenuPopover = ({ avatar }: AccountMenuPopoverProps) => {
  const [open, setOpen] = useState(false);

  const t = useTranslations('header.navigation-menu-items');

  const pathname = usePathname();

  const data = getAccountMenuItems(t);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger
        asChild
        className="mx-1 sm:mx-0"
      >
        <Button
          variant="secondary"
          className="group !h-10 !w-10 flex-shrink-0 rounded-full p-0"
          data-testid="header-user-icon"
        >
          <Avatar
            src={AVATARS[avatar! as keyof typeof AVATARS]}
            className="h-full w-full rounded-full"
            imgClassName="object-cover object-center rounded-full"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        dir="ltr"
        className="z-[1000]"
        contentClassName="max-h-[350px] w-[180px] min-h-[254px]"
        align="end"
      >
        {data.map(({ name, Icon, href }, index) => (
          <TabItem
            key={href}
            className={cn(
              'flex min-h-[30px] w-full flex-row items-center justify-between rounded-none p-2 rtl:flex-row-reverse',
              index === 0 && 'rounded-t'
            )}
            onClick={() => setOpen(false)}
            isActive={pathname === href}
            data-testid="account-menu-item"
            asChild
          >
            <UnStyledLink href={href}>
              <Icon
                className="h-5 w-5"
                {...(Icon as React.HTMLAttributes<SVGElement>)}
              />
              <span className="w-full px-1.5 text-left rtl:text-right">{name}</span>
            </UnStyledLink>
          </TabItem>
        ))}
        <SignOutButton className="min-h-[30px] w-full flex-row rounded-t-none p-2 rtl:flex-row-reverse" />
      </PopoverContent>
    </Popover>
  );
};

export default AccountMenuPopover;
